import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { dateFormatToDisplay } from '../../../../utils/dateUtils';

const getMealName = (meal, language) => {
  return meal?.details?.find((detail) => detail.language === language)?.name ?? '-';
};

const getScheduleKey = (schedule) => {
  return [
    schedule?.date ?? schedule?.dateOfSchedule,
    schedule?.scheduleType,
    schedule?.mealPlanId ?? schedule?.mealPlan?.id ?? schedule?.mealPlan,
    schedule?.externalId ?? schedule?.id,
  ].join('-');
};

const DeleteMealDialog = ({
  open,
  onClose,
  onConfirm,
  meal,
  language = 'English',
  requiresScheduleCleanup = false,
  isDeleting,
  futureSchedules = [],
  isLoadingFutureSchedules = false,
  isFutureSchedulesError = false,
  onDeleteSchedule,
  deletingScheduleKey = null,
}) => {
  const selectedLanguageName = getMealName(meal, language);
  const mealName = selectedLanguageName !== '-' ? selectedLanguageName : 'this food box';
  const hasFutureSchedules = futureSchedules.length > 0;
  const shouldDisableDeleteMeal =
    isDeleting ||
    (requiresScheduleCleanup && isLoadingFutureSchedules) ||
    (requiresScheduleCleanup && isFutureSchedulesError) ||
    (requiresScheduleCleanup && hasFutureSchedules) ||
    deletingScheduleKey !== null;

  return (
    <Dialog
      open={open}
      onClose={isDeleting || deletingScheduleKey !== null ? undefined : onClose}
      fullWidth
      maxWidth='md'
    >
      <DialogTitle sx={{ pr: 7 }}>
        Delete food box
        <IconButton
          onClick={onClose}
          disabled={isDeleting || deletingScheduleKey !== null}
          sx={{ position: 'absolute', right: 16, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant='s16_w500'>
          Are you sure you want to delete <strong>{mealName}</strong>?
        </Typography>
        {requiresScheduleCleanup ? (
          <>
            <Typography variant='s16_w500' mt={3}>
              Future schedules must be deleted before the meal is deleted.
            </Typography>

            {isLoadingFutureSchedules ? (
              <Typography variant='s16_w500' mt={2} color='#6B7280'>
                Loading future schedules...
              </Typography>
            ) : isFutureSchedulesError ? (
              <Typography variant='s16_w500' mt={2} color='error'>
                Unable to load future schedules right now. Please try again before deleting this food box.
              </Typography>
            ) : hasFutureSchedules ? (
              <TableContainer sx={{ mt: 2, border: '1px solid #E5E7EB', borderRadius: '12px' }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Schedule type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Meal plan</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align='right'>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {futureSchedules.map((schedule) => {
                      const scheduleKey = getScheduleKey(schedule);
                      const isDeletingSchedule = deletingScheduleKey === scheduleKey;

                      return (
                        <TableRow key={scheduleKey} hover>
                          <TableCell>
                            {dayjs(schedule.date ?? schedule.dateOfSchedule).format(
                              dateFormatToDisplay
                            )}
                          </TableCell>
                          <TableCell>{schedule.scheduleType}</TableCell>
                          <TableCell>
                            {schedule.mealPlanName ??
                              schedule.mealPlanId ??
                              schedule.mealPlan?.name ??
                              schedule.mealPlan ??
                              '-'}
                          </TableCell>
                          <TableCell align='right'>
                            <Button
                              color='error'
                              variant='outlined'
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => onDeleteSchedule(schedule)}
                              disabled={isDeletingSchedule || deletingScheduleKey !== null}
                              aria-label={`delete schedule ${scheduleKey}`}
                            >
                              Delete schedule
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant='s16_w500' mt={2} color='#6B7280'>
                No future schedules found. You can delete this food box.
              </Typography>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant='outlined'
          onClick={onClose}
          disabled={isDeleting || deletingScheduleKey !== null}
        >
          Cancel
        </Button>
        <Button
          color='error'
          variant='contained'
          onClick={onConfirm}
          disabled={shouldDisableDeleteMeal}
        >
          Delete food box
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteMealDialog;
export { getScheduleKey };
